import { PRIVATE_MIRROR, feedKey } from '../lib/feedContext';
/**
 * Мои конторы: где есть счёт, где порезали, где я ставлю слишком часто.
 *
 * Состояние счёта и пометка живут у человека: лимит — свойство пары
 * «человек и контора», и меняется он молча. Прокси той же пары владелец велел
 * везти ещё и на сервер, в профиль: на чужой машине дорога должна быть той же.
 * Сканер без отметки «закрыт» каждый день показывает строки, которые он не
 * может взять, и каждая такая строка стоит ему секунд.
 *
 * Три состояния счёта, и «порезан» — не то же, что «закрыт»:
 *
 *   `open`    — работает как обычно;
 *   `limited` — принимает, но мало. Строку показываем с предупреждением:
 *               в порезанной конторе часто берут вторую ногу мелкой суммой;
 *   `closed`  — счёта нет или он заблокирован. Строку не показываем вовсе,
 *               но считаем в скрытом: молчаливая пропажа неотличима от
 *               «вилок нет» (см. `filters.ts`).
 *
 * Про ротацию. Ставить всё время в одну контору — самый быстрый способ получить
 * порезку: арбитражный поток видно по самой его форме. «Разводить» ставки может
 * только человек, но заметить перекос обязаны мы: он смотрит на ленту, а не на
 * свою историю. Поэтому считаем ДОЛЮ конторы в его ставках за неделю и говорим,
 * когда она стала большинством. Совета «поставь в другую» здесь нет: другая
 * нога вилки не выбирается, выбирается сама вилка.
 *
 * Потолок суммы (`caps`) живёт не здесь, а в `store/settings.ts`, где он и был:
 * его читает расчёт строки. Второй дом для того же числа означал бы два разных
 * ответа на вопрос «сколько влезает».
 *
 * Правится это всё в студии фильтров, на плитке самой конторы (карандаш в
 * разделе «конторы»). До 12.08 под лентой стояла отдельная полка «мои конторы»:
 * справочник, к которому возвращаются раз в неделю, занимал место у цен,
 * которые смотрят каждую секунду, — и стоял он отдельно от того места, где
 * человек и так отмечает, где у него есть счёт.
 */

import { create } from 'zustand';

import { doors as publishDoors, roadSecret, roads as publishRoads } from '../confirm/publish';
import { apiUrl } from '../lib/build';
import { deskTakesOpen } from './desk';
import { formatDoor, parseDoor } from './hosts';
import { parseRoadLine } from './roads';

export type Standing = 'open' | 'limited' | 'closed';

/**
 * Один матч или исход, который человек не берёт у этой кассы.
 *
 * Ключ — серверный `ev` и код площадки, не id вилки: соседний тотал — другая
 * строка, а боль про счёт на этом матче та же. Без `ev` правило не создаём.
 * `market` / `side` пусты — весь матч у кассы; иначе только этот исход.
 */
export interface VenueExclusion {
  ev: string;
  market?: string;
  side?: string;
  /** «PARIVISION — Heroic · тотал 24.5» — то, что видит человек, не код. */
  label: string;
  /** Когда поставил, мс эпохи. */
  at: number;
  /** Держать после автосъёма: повторный матч тех же имён тоже не брать. */
  keep?: boolean;
  /** Когда в кадре последний раз видели пару (касса × ev). Для автосъёма. */
  seen?: number;
}

export interface Account {
  standing: Standing;
  /** Когда человек это отметил, мс эпохи. Порезка — событие, у него есть дата. */
  at: number;
  /** Своя пометка: «только с телефона», «верификация», «режут после 3к». */
  note: string;
  /**
   * Свой домен кассы. Пусто — клик открывает канон. На сервер не едет:
   * это дверь человека, не настройка ленты.
   */
  door: string;
  /**
   * Прокси этой конторы, как человек вставил. Пусто — открывать прямо.
   * Логин и пароль здесь же: владелец велел не прятать и везти с профилем.
   */
  proxy?: string;
  /**
   * Матчи и исходы, которые у этой кассы не беру. Шрам счёта, не фильтр на
   * сегодня: живёт рядом с порезкой и пометкой, не в сите и не в профиле.
   */
  exclude?: VenueExclusion[];
}

/** Одна ставка в конторе: только площадка и время. Суммы здесь не нужны. */
export interface Use {
  venue: string;
  at: number;
}

/** Окно ротации. Неделя — потому что порезки живут неделями, а не часами. */
export const WINDOW_DAYS = 7;
const WINDOW_MS = WINDOW_DAYS * 24 * 60 * 60 * 1000;

/** Сколько ставок в окне должно набраться, прежде чем доля вообще что-то значит. */
export const ENOUGH_USES = 6;

/** С какой доли контора считается перегруженной. */
export const CROWDED = 0.5;

export interface Pressure {
  /** Ставок в этой конторе за окно. */
  mine: number;
  /** Всего ставок за окно. */
  total: number;
  /** Доля 0…1. */
  share: number;
  /** Стоит ли об этом говорить: доля большая И замеров достаточно. */
  crowded: boolean;
}

export function pressureOf(uses: readonly Use[], venue: string, now: number): Pressure {
  const live = uses.filter((one) => now - one.at <= WINDOW_MS);
  const mine = live.filter((one) => one.venue === venue).length;
  const total = live.length;
  const share = total === 0 ? 0 : mine / total;
  return { mine, total, share, crowded: total >= ENOUGH_USES && share > CROWDED };
}

/** Слова для перекоса. Число и только число: советов человеку здесь не даём. */
export function pressureText(one: Pressure): string {
  return `${one.mine} из ${one.total} ваших ставок за неделю прошли здесь`;
}

export interface AccountsState {
  /** Код площадки → счёт. Нет записи — значит `open` и ничего не отмечено. */
  accounts: Record<string, Account>;
  uses: Use[];

  standingOf: (venue: string) => Standing;
  setStanding: (venue: string, standing: Standing) => void;
  setNote: (venue: string, note: string) => void;
  setDoor: (venue: string, raw: string) => void;
  setProxy: (venue: string, raw: string) => void;
  addExclude: (venue: string, rule: VenueExclusion) => void;
  dropExclude: (venue: string, index: number) => void;
  keepExclude: (venue: string, index: number, keep: boolean) => void;
  /** Снять протухшие исключения: пары нет на проводе дольше окна и нет `keep`. */
  touchExclusions: (live: ReadonlySet<string>, now?: number) => void;
  /** Отметить ставку в конторе. Зовётся оттуда же, где подтверждается нога. */
  noteUse: (venue: string) => void;
  forget: (venue: string) => void;
}

/** Сколько шрамов помним на одну кассу. Это не каталог лиги. */
export const EXCLUDE_CAP = 30;

/** Через сколько без живой пары (касса × ev) снимаем бан, если нет `keep`. */
export const EXCLUDE_DROP_MS = 36 * 60 * 60 * 1000;

export function excludeKey(one: Pick<VenueExclusion, 'ev' | 'market' | 'side'>): string {
  return `${one.ev}\t${one.market ?? ''}\t${one.side ?? ''}`;
}

export function livePair(venue: string, ev: string): string {
  return `${venue}\t${ev}`;
}

export function sameExclude(
  a: Pick<VenueExclusion, 'ev' | 'market' | 'side'>,
  b: Pick<VenueExclusion, 'ev' | 'market' | 'side'>,
): boolean {
  return excludeKey(a) === excludeKey(b);
}

/** Подпись для карандаша: матч и исход, не сырой `ev`. */
export function excludeLabel(match: string, outcome: string): string {
  const title = match.trim();
  const pick = outcome.trim();
  if (title === '') return pick;
  if (pick === '') return title;
  return `${title} · ${pick}`;
}

export function matchTitle(participants: readonly string[] | undefined): string {
  const names = (participants ?? []).map((one) => one.trim()).filter((one) => one !== '');
  if (names.length >= 2) return `${names[0]} — ${names[1]}`;
  return names[0] ?? '';
}

/** Честная фраза пустого экрана: «исключён исход у Леона», не «вилок нет». */
export function excludeSaid(
  accounts: Record<string, Pick<Account, 'exclude'>>,
  nameOf: (venue: string) => string,
): string | null {
  const hits: { venue: string; outcome: boolean }[] = [];
  for (const [venue, one] of Object.entries(accounts)) {
    for (const rule of one.exclude ?? []) {
      hits.push({ venue, outcome: Boolean(rule.market) });
    }
  }
  const first = hits[0];
  if (first === undefined) return null;
  const kind = first.outcome ? 'исход' : 'матч';
  const who = nameOf(first.venue);
  if (hits.length === 1) return `исключён ${kind} у ${who}`;
  return `исключён ${kind} у ${who} и ещё ${hits.length - 1}`;
}

const KEY = PRIVATE_MIRROR ? feedKey('forq.next.accounts') : 'forq.next.accounts';

/** Сколько ставок помним. Двух недель хватает окну, остальное — балласт. */
const USES_KEPT = 400;

interface Saved {
  accounts?: Record<string, Account>;
  uses?: Use[];
}

const STANDINGS: readonly Standing[] = ['open', 'limited', 'closed'];

/**
 * Разобрать то, что лежит в хранилище браузера.
 *
 * Читаем чужую запись как чужую: там мог остаться формат прошлой недели, а
 * могла остаться и правка руками. Одна кривая запись не имеет права уронить
 * ленту — она только лишает нас знания про одну контору.
 */
export function readAccounts(body: unknown): Saved {
  if (typeof body !== 'object' || body === null) return {};
  const box = body as { accounts?: unknown; uses?: unknown };
  const accounts: Record<string, Account> = {};
  if (typeof box.accounts === 'object' && box.accounts !== null) {
    for (const [venue, value] of Object.entries(box.accounts)) {
      if (typeof value !== 'object' || value === null) continue;
      const one = value as Partial<Account>;
      if (one.standing === undefined || !STANDINGS.includes(one.standing)) continue;
      const doorRaw = typeof one.door === 'string' ? one.door : '';
      const parsed = parseDoor(doorRaw);
      const proxyRaw = typeof one.proxy === 'string' ? one.proxy.trim() : '';
      const proxyGot = parseRoadLine(proxyRaw);
      const exclude = readExclude((one as { exclude?: unknown }).exclude);
      accounts[venue] = {
        standing: one.standing,
        at: typeof one.at === 'number' ? one.at : 0,
        note: typeof one.note === 'string' ? one.note : '',
        door: parsed === null ? '' : formatDoor(parsed),
        ...(proxyGot.ok ? { proxy: proxyRaw } : {}),
        ...(exclude.length > 0 ? { exclude } : {}),
      };
    }
  }
  const uses = Array.isArray(box.uses)
    ? box.uses.filter(
        (one): one is Use =>
          typeof one === 'object' &&
          one !== null &&
          typeof (one as Use).venue === 'string' &&
          typeof (one as Use).at === 'number',
      )
    : [];
  return { accounts, uses };
}

function load(): Saved {
  try {
    const raw = localStorage.getItem(KEY);
    return raw === null ? {} : readAccounts(JSON.parse(raw));
  } catch {
    return {};
  }
}

const saved = load();

function blank(at = Date.now()): Account {
  return { standing: 'open', at, note: '', door: '' };
}

function vacant(one: Account): boolean {
  return (
    one.standing === 'open' &&
    one.note === '' &&
    !one.door &&
    one.proxy === undefined &&
    (one.exclude === undefined || one.exclude.length === 0)
  );
}

function readExclude(raw: unknown): VenueExclusion[] {
  if (!Array.isArray(raw)) return [];
  const out: VenueExclusion[] = [];
  for (const one of raw) {
    if (typeof one !== 'object' || one === null) continue;
    const body = one as Partial<VenueExclusion>;
    const ev = typeof body.ev === 'string' ? body.ev.trim() : '';
    if (ev === '') continue;
    const label = typeof body.label === 'string' && body.label.trim() !== '' ? body.label : ev;
    const at = typeof body.at === 'number' && Number.isFinite(body.at) ? body.at : 0;
    const next: VenueExclusion = { ev, label, at };
    if (typeof body.market === 'string' && body.market !== '') next.market = body.market;
    if (typeof body.side === 'string' && body.side !== '') next.side = body.side;
    if (body.keep === true) next.keep = true;
    if (typeof body.seen === 'number' && Number.isFinite(body.seen)) next.seen = body.seen;
    out.push(next);
  }
  return trimExclude(out);
}

function trimExclude(list: VenueExclusion[]): VenueExclusion[] {
  if (list.length <= EXCLUDE_CAP) return list;
  const kept = list.filter((one) => one.keep);
  const rest = list.filter((one) => !one.keep).sort((a, b) => a.at - b.at);
  const room = Math.max(0, EXCLUDE_CAP - kept.length);
  const extra = rest.slice(Math.max(0, rest.length - room));
  if (kept.length + extra.length <= EXCLUDE_CAP) {
    return [...kept, ...extra].sort((a, b) => a.at - b.at);
  }
  return [...kept, ...extra]
    .sort((a, b) => a.at - b.at)
    .slice(Math.max(0, kept.length + extra.length - EXCLUDE_CAP));
}

/**
 * Снять протухшие и отметить живые пары. Чистая функция: стор пишет только
 * если что-то изменилось — иначе каждый кадр ленты стучал бы в localStorage.
 */
export function pruneExclusions(
  accounts: Record<string, Account>,
  live: ReadonlySet<string>,
  now: number,
): Record<string, Account> | null {
  let changed = false;
  const next: Record<string, Account> = {};
  for (const [venue, one] of Object.entries(accounts)) {
    const list = one.exclude;
    if (list === undefined || list.length === 0) {
      next[venue] = one;
      continue;
    }
    const kept: VenueExclusion[] = [];
    for (const rule of list) {
      const pair = livePair(venue, rule.ev);
      if (live.has(pair)) {
        if (rule.seen === undefined || now - rule.seen >= 60 * 60 * 1000) {
          kept.push({ ...rule, seen: now });
          changed = true;
        } else {
          kept.push(rule);
        }
        continue;
      }
      if (rule.keep) {
        kept.push(rule);
        continue;
      }
      const last = rule.seen ?? rule.at;
      if (now - last >= EXCLUDE_DROP_MS) {
        changed = true;
        continue;
      }
      kept.push(rule);
    }
    if (kept.length !== list.length) changed = true;
    const patched: Account = { ...one };
    if (kept.length === 0) delete patched.exclude;
    else patched.exclude = kept;
    if (vacant(patched)) {
      changed = true;
      continue;
    }
    next[venue] = patched;
  }
  return changed ? next : null;
}

export function mapDoors(accounts: Record<string, Account>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [venue, one] of Object.entries(accounts)) {
    if (one.door !== '') out[venue] = one.door;
  }
  return out;
}

export function mapProxies(accounts: Record<string, Account>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [venue, one] of Object.entries(accounts)) {
    if (one.proxy) out[venue] = one.proxy;
  }
  return out;
}

let publishedSecrets = new Set<string>();

/** PAC снимаем только с живого стола, не из‑за забытого тумблера в storage. */
export function roadsDeskOn(): boolean {
  return deskTakesOpen();
}

/** Карта дверей и стол — иначе секрет в SW есть, а PAC не ставится. */
export function flushRoads(accounts?: Record<string, Account>): void {
  if (PRIVATE_MIRROR) return;
  const have = accounts ?? useAccounts.getState().accounts;
  publishRoads({
    doors: mapDoors(have),
    books: Object.keys(have),
    deskOn: roadsDeskOn(),
  });
}

function flushProxySecretsFrom(accounts: Record<string, Account>): void {
  if (PRIVATE_MIRROR) return;
  const next = mapProxies(accounts);
  const venues = new Set([...publishedSecrets, ...Object.keys(next)]);
  for (const venue of venues) roadSecret(venue, next[venue] ?? '');
  publishedSecrets = new Set(Object.keys(next));
  flushRoads(accounts);
}

/** Отдать расширению все строки, что лежат в счетах. */
export function flushProxySecrets(): void {
  flushProxySecretsFrom(useAccounts.getState().accounts);
}

let proxyTimer: ReturnType<typeof setTimeout> | null = null;

function pushProxies(): void {
  if (PRIVATE_MIRROR || typeof fetch === 'undefined') return;
  if (proxyTimer !== null) clearTimeout(proxyTimer);
  proxyTimer = setTimeout(() => {
    void fetch(apiUrl('/v1/profile'), {
      method: 'PUT',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ profile: { proxies: mapProxies(useAccounts.getState().accounts) } }),
    }).catch(() => {
      // Аноним и обрыв — строка останется в браузере.
    });
  }, 400);
}

/** Сервер побеждает локальное: человек открыл чужую машину своими дорогами. */
export function applyProxies(incoming: Record<string, unknown>): void {
  const want: Record<string, string> = {};
  for (const [venue, line] of Object.entries(incoming)) {
    if (typeof line !== 'string') continue;
    const trimmed = line.trim();
    if (trimmed === '' || !parseRoadLine(trimmed).ok) continue;
    want[venue] = trimmed;
  }
  const accounts = { ...useAccounts.getState().accounts };
  const venues = new Set([...Object.keys(accounts), ...Object.keys(want)]);
  for (const venue of venues) {
    const was = accounts[venue] ?? blank();
    const next: Account = { ...was };
    if (want[venue]) next.proxy = want[venue];
    else delete next.proxy;
    if (vacant(next)) delete accounts[venue];
    else accounts[venue] = next;
  }
  useAccounts.setState({ accounts });
  try {
    localStorage.setItem(
      KEY,
      JSON.stringify({ accounts, uses: useAccounts.getState().uses } satisfies Saved),
    );
  } catch {
    //
  }
  if (!PRIVATE_MIRROR) publishDoors(mapDoors(accounts));
  flushProxySecretsFrom(accounts);
}

export const useAccounts = create<AccountsState>((set, get) => {
  const store = (): void => {
    try {
      localStorage.setItem(
        KEY,
        JSON.stringify({ accounts: get().accounts, uses: get().uses } satisfies Saved),
      );
    } catch {
      // Приватный режим и переполненное хранилище — не повод падать.
    }
    if (!PRIVATE_MIRROR) publishDoors(mapDoors(get().accounts));
    flushProxySecretsFrom(get().accounts);
    pushProxies();
  };

  return {
    accounts: saved.accounts ?? {},
    uses: saved.uses ?? [],

    standingOf: (venue) => get().accounts[venue]?.standing ?? 'open',

    setStanding: (venue, standing) => {
      const accounts = { ...get().accounts };
      const was = accounts[venue] ?? blank();
      const next: Account = { ...was, standing, at: Date.now() };
      if (vacant(next)) delete accounts[venue];
      else accounts[venue] = next;
      set({ accounts });
      store();
    },

    setNote: (venue, note) => {
      const accounts = { ...get().accounts };
      const was = accounts[venue] ?? blank();
      const next: Account = { ...was, note };
      if (vacant(next)) delete accounts[venue];
      else accounts[venue] = next;
      set({ accounts });
      store();
    },

    setDoor: (venue, raw) => {
      const trimmed = raw.trim();
      if (trimmed !== '' && parseDoor(trimmed) === null) return;
      const accounts = { ...get().accounts };
      const was = accounts[venue] ?? blank();
      const parsed = parseDoor(trimmed);
      const next: Account = { ...was, door: parsed === null ? '' : formatDoor(parsed) };
      if (vacant(next)) delete accounts[venue];
      else accounts[venue] = next;
      set({ accounts });
      store();
    },

    setProxy: (venue, raw) => {
      const trimmed = raw.trim();
      const accounts = { ...get().accounts };
      const was = accounts[venue] ?? blank();
      const next: Account = { ...was };
      if (trimmed === '') {
        delete next.proxy;
      } else {
        const parsed = parseRoadLine(trimmed);
        if (!parsed.ok) return;
        next.proxy = trimmed;
      }
      if (vacant(next)) delete accounts[venue];
      else accounts[venue] = next;
      set({ accounts });
      store();
    },

    addExclude: (venue, rule) => {
      const ev = rule.ev.trim();
      if (ev === '') return;
      const accounts = { ...get().accounts };
      const was = accounts[venue] ?? blank();
      const incoming: VenueExclusion = {
        ev,
        label: rule.label.trim() === '' ? ev : rule.label,
        at: rule.at,
        ...(rule.market ? { market: rule.market } : {}),
        ...(rule.side ? { side: rule.side } : {}),
        ...(rule.keep ? { keep: true } : {}),
        seen: rule.seen ?? rule.at,
      };
      const prev = was.exclude ?? [];
      const hit = prev.findIndex((one) => sameExclude(one, incoming));
      const list =
        hit >= 0
          ? prev.map((one, i) => (i === hit ? { ...one, ...incoming, keep: one.keep } : one))
          : [...prev, incoming];
      const next: Account = { ...was, exclude: trimExclude(list) };
      accounts[venue] = next;
      set({ accounts });
      store();
    },

    dropExclude: (venue, index) => {
      const accounts = { ...get().accounts };
      const was = accounts[venue];
      if (was === undefined || was.exclude === undefined) return;
      const list = was.exclude.filter((_, i) => i !== index);
      const next: Account = { ...was };
      if (list.length === 0) delete next.exclude;
      else next.exclude = list;
      if (vacant(next)) delete accounts[venue];
      else accounts[venue] = next;
      set({ accounts });
      store();
    },

    keepExclude: (venue, index, keep) => {
      const accounts = { ...get().accounts };
      const was = accounts[venue];
      const list = was?.exclude;
      if (was === undefined || list === undefined || list[index] === undefined) return;
      const nextList = list.map((one, i) => {
        if (i !== index) return one;
        const patched = { ...one };
        if (keep) patched.keep = true;
        else delete patched.keep;
        return patched;
      });
      accounts[venue] = { ...was, exclude: nextList };
      set({ accounts });
      store();
    },

    touchExclusions: (live, now = Date.now()) => {
      const next = pruneExclusions(get().accounts, live, now);
      if (next === null) return;
      set({ accounts: next });
      store();
    },

    noteUse: (venue) => {
      set({ uses: [...get().uses, { venue, at: Date.now() }].slice(-USES_KEPT) });
      store();
    },

    forget: (venue) => {
      const accounts = { ...get().accounts };
      delete accounts[venue];
      set({ accounts });
      store();
    },
  };
});

if (!PRIVATE_MIRROR) publishDoors(mapDoors(saved.accounts ?? {}));
